/** Lightweight MSME operations quests — picture-first, not Scope-stepper-first. */

export const OPS_QUEST_SEEN_STORAGE_KEY = 'ci_ops_quests_seen_v1';

export type OperationsQuestId =
  | 'power_up'
  | 'fuel_check'
  | 'water_drop'
  | 'name_plant'
  | 'save_picture';

export type OperationsQuest = {
  id: OperationsQuestId;
  title: string;
  description: string;
  complete: boolean;
  points: number;
  /** Scroll / focus target hint for the UI */
  focus: 'electricity' | 'fuel' | 'water' | 'units' | 'save';
};

export type OperationsQuestBoard = {
  quests: OperationsQuest[];
  completedCount: number;
  totalCount: number;
  percent: number;
  level: number;
  levelLabel: string;
  levelCaption: string;
  nextQuest: OperationsQuest | null;
};

export type OperationsQuestInput = {
  powerKwhPerMonth?: number;
  fuelLitersPerMonth?: number;
  waterKlPerMonth?: number;
  primaryUnitName?: string;
  hasSavedEstimate?: boolean;
};

const DEFAULT_UNIT_NAMES = new Set(['', 'unit 1', 'main plant', 'plant 1', 'site 1']);

export function isNamedPlant(name?: string): boolean {
  const normalized = String(name || '').trim().toLowerCase();
  if (!normalized) return false;
  return !DEFAULT_UNIT_NAMES.has(normalized);
}

export function deriveOperationsQuests(input: OperationsQuestInput): OperationsQuestBoard {
  const quests: OperationsQuest[] = [
    {
      id: 'power_up',
      title: 'Power up',
      description: 'Add monthly electricity use (kWh) from your bill or meter.',
      complete: Number(input.powerKwhPerMonth) > 0,
      points: 20,
      focus: 'electricity',
    },
    {
      id: 'fuel_check',
      title: 'Fuel check',
      description: 'Add diesel, petrol, or CNG litres for gensets or vehicles.',
      complete: Number(input.fuelLitersPerMonth) > 0,
      points: 20,
      focus: 'fuel',
    },
    {
      id: 'water_drop',
      title: 'Water drop',
      description: 'Optional — add process or utility water (kL / month).',
      complete: Number(input.waterKlPerMonth) > 0,
      points: 10,
      focus: 'water',
    },
    {
      id: 'name_plant',
      title: 'Name your plant',
      description: 'Give your main site a real name so reports stay clear.',
      complete: isNamedPlant(input.primaryUnitName),
      points: 15,
      focus: 'units',
    },
    {
      id: 'save_picture',
      title: 'Save your picture',
      description: 'Calculate once to lock in the live emissions estimate.',
      complete: Boolean(input.hasSavedEstimate),
      points: 35,
      focus: 'save',
    },
  ];

  const completedCount = quests.filter((q) => q.complete).length;
  const totalCount = quests.length;
  const percent = Math.round((completedCount / totalCount) * 100);
  const levelMeta: Record<number, { label: string; caption: string }> = {
    1: { label: 'Starter', caption: 'Add one energy number to level up.' },
    2: { label: 'Powered', caption: 'Nice — add fuel or name your plant next.' },
    3: { label: 'Mapped', caption: 'Almost there — save to earn the badge.' },
    4: { label: 'Ops champ', caption: 'Picture locked. Advanced detail is optional.' },
  };
  const band =
    completedCount >= 4 ? 4 : completedCount >= 3 ? 3 : completedCount >= 2 ? 2 : 1;

  return {
    quests,
    completedCount,
    totalCount,
    percent,
    level: band,
    levelLabel: levelMeta[band].label,
    levelCaption: levelMeta[band].caption,
    nextQuest: quests.find((q) => !q.complete) ?? null,
  };
}

export function loadSeenOpsQuests(): Set<string> {
  if (typeof window === 'undefined') {
    return new Set();
  }
  try {
    const raw = window.localStorage.getItem(OPS_QUEST_SEEN_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

export function markOpsQuestSeen(id: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const next = loadSeenOpsQuests();
  next.add(id);
  window.localStorage.setItem(OPS_QUEST_SEEN_STORAGE_KEY, JSON.stringify(Array.from(next)));
}

export function detectNewOpsQuestCelebrations(
  board: OperationsQuestBoard
): Array<{ id: string; title: string; description: string; points: number }> {
  const seen = loadSeenOpsQuests();
  return board.quests
    .filter((quest) => quest.complete && !seen.has(quest.id))
    .map((quest) => ({
      id: quest.id,
      title: quest.title,
      description: quest.description,
      points: quest.points,
    }));
}
